import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Search } from "lucide-react";
import { filterUsersAsync } from "../../Admin-Api/FilterUsersAsync";

import {
  GetAllUsersAsync,
  AllUsersData,
} from "../../Admin-Api/GetAllUsersAsync";

import {
  GetAllDeletedUsersDataAsync,
  AllDeletedUsersData,
} from "../../Admin-Api/GetAllDeletedUsersDataAsync";

import { deleteUserAdmin } from "../../Admin-Api/DeleteUserAsync";

import {
  updateUserAsync,
  UpdateUserPayload,
} from "../../Admin-Api/UpdateUserAsync";

import EditIcon from "../../assets/icons/edit.svg";

type UsersView = "users" | "deleted";

const AllUsers: React.FC = () => {
  const [usersView, setUsersView] = useState<UsersView>("users");

  const [users, setUsers] = useState<AllUsersData[]>([]);
  const [deletedUsers, setDeletedUsers] = useState<
    AllDeletedUsersData[]
  >([]);

  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // const pageSize = 20;
  const [pageSize, setPageSize] = useState(20);
  // const totalPages = Math.ceil(totalRecords / pageSize);
  const totalPages = Math.ceil(totalRecords / pageSize);
  const [searchText, setSearchText] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");

  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectAllGlobal, setSelectAllGlobal] = useState(false);
  const [excludedUsers, setExcludedUsers] = useState<number[]>([]);

  const [confirmDeleteOpen, setConfirmDeleteOpen] =
    useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] =
    useState<AllUsersData | null>(null);

  const [editForm, setEditForm] = useState<UpdateUserPayload>({
    userName: "",
    email: "",
    phoneNumber: "",
  });

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not Provided";

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return "Not Provided";
    }

    return date.toLocaleDateString("en-GB");
  };

  const fetchUsers = async (
    page: number,
    search: string = searchText
  ) => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      if (!token) {
        toast.error("Session expired. Please login again.");
        return;
      }

      const response = await GetAllUsersAsync(
        page,
        pageSize,
        search,
        token
      );

      setUsers(response.data || []);
      setTotalRecords(response.totalRecords || 0);
      setPageNumber(page);
    } catch (error) {
      console.error("Failed to fetch users:", error);

      setUsers([]);
      setTotalRecords(0);

      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredUsers = async (
    page: number,
    role: string
  ) => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      if (!token) {
        toast.error("Session expired. Please login again.");
        return;
      }

      const response = await filterUsersAsync(
        role,
        page,
        pageSize,
        token
      );

      setUsers(response.data || []);
      setTotalRecords(response.totalRecords || 0);
      setPageNumber(page);
    } catch (error) {
      console.error("Failed to filter users:", error);

      setUsers([]);
      setTotalRecords(0);

      toast.error("Failed to filter users");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (role: string) => {
    setSelectedRole(role);
    setPageNumber(1);

    setSelectedUsers([]);
    setSelectAllGlobal(false);
    setExcludedUsers([]);
  };

  // -------------------------------------------------------
  // FETCH DELETED USERS
  // -------------------------------------------------------

  const fetchDeletedUsers = async (page: number) => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      if (!token) {
        toast.error("Session expired. Please login again.");
        return;
      }

      const response = await GetAllDeletedUsersDataAsync(
        page,
        pageSize,
        token
      );

      setDeletedUsers(response.data || []);
      setTotalRecords(response.totalRecords || 0);
      setPageNumber(page);
    } catch (error) {
      console.error("Failed to fetch deleted users:", error);

      setDeletedUsers([]);
      setTotalRecords(0);

      toast.error("Failed to fetch deleted users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (usersView === "users") {
        if (selectedRole === "all") {
          fetchUsers(1, searchText);
        } else {
          fetchFilteredUsers(1, selectedRole);
        }
      } else {
        fetchDeletedUsers(1);
      }
    }, 500);

    return () => window.clearTimeout(timer);
  }, [usersView, searchText, pageSize, selectedRole]);


  const filteredDeletedUsers = deletedUsers.filter((user) => {
    const query = searchText.trim().toLowerCase();

    if (!query) return true;

    return (
      String(user.userName || "")
        .toLowerCase()
        .includes(query) ||
      String(user.email || "")
        .toLowerCase()
        .includes(query) ||
      String(user.phoneNumber || "")
        .toLowerCase()
        .includes(query)
    );
  });

  // -------------------------------------------------------
  // CHANGE INNER VIEW
  // -------------------------------------------------------

  const handleUsersViewChange = (view: UsersView) => {
    if (view === usersView) return;

    setUsersView(view);
    setSearchText("");
    setPageNumber(1);
    setTotalRecords(0);

    setSelectedUsers([]);
    setSelectAllGlobal(false);
    setExcludedUsers([]);

    setConfirmDeleteOpen(false);
    setEditModalOpen(false);
    setEditingUser(null);

    if (view === "users") {
      setDeletedUsers([]);
    } else {
      setUsers([]);
    }
  };

  // -------------------------------------------------------
  // EDIT USER
  // -------------------------------------------------------

  const handleEditClick = (user: AllUsersData) => {
    setEditingUser(user);

    setEditForm({
      userName: user.userName || "",
      email: user.email || "",
      phoneNumber: user.phoneNumber || "",
    });

    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    if (updating) return;

    setEditModalOpen(false);
    setEditingUser(null);

    setEditForm({
      userName: "",
      email: "",
      phoneNumber: "",
    });
  };

  const handleEditInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;

    if (name === "phoneNumber") {
      let cleaned = value.replace(/[^\d+]/g, "");

      if (cleaned.includes("+")) {
        cleaned =
          "+" +
          cleaned
            .replace(/\+/g, "")
            .replace(/^\+/, "");
      }

      const digitsOnly = cleaned.replace(/\+/g, "");
      if (digitsOnly.length > 15) {
        return;
      }

      setEditForm((prev) => ({
        ...prev,
        phoneNumber: cleaned,
      }));
      return;
    }

    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateUser = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (!editingUser) return;

    const userName = editForm.userName.trim();
    const email = editForm.email.trim();
    const phoneNumber = editForm.phoneNumber.trim();

    if (!userName) {
      toast.error("Please enter the user name.");
      return;
    }

    if (!email) {
      toast.error("Please enter the email address.");
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        toast.error("Session expired. Please login again.");
        return;
      }

      setUpdating(true);

      const payload: UpdateUserPayload = {
        userName,
        email,
        phoneNumber,
      };

      await updateUserAsync(editingUser.id, payload, token);

      setUsers((previousUsers) =>
        previousUsers.map((user) =>
          user.id === editingUser.id
            ? {
              ...user,
              userName,
              email,
              phoneNumber,
            }
            : user
        )
      );

      toast.success("User updated successfully");

      setEditModalOpen(false);
      setEditingUser(null);

      setEditForm({
        userName: "",
        email: "",
        phoneNumber: "",
      });

      await fetchUsers(pageNumber, searchText);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update user";

      toast.error(message);
    } finally {
      setUpdating(false);
    }
  };

  // -------------------------------------------------------
  // SELECTION
  // -------------------------------------------------------

  const isChecked = (userId: number) => {
    return selectAllGlobal
      ? !excludedUsers.includes(userId)
      : selectedUsers.includes(userId);
  };

  const isAllSelected =
    users.length > 0 &&
    users.every((user) =>
      selectAllGlobal
        ? !excludedUsers.includes(user.id)
        : selectedUsers.includes(user.id)
    );

  const toggleSelectUser = (userId: number) => {
    if (selectAllGlobal) {
      setExcludedUsers((previous) =>
        previous.includes(userId)
          ? previous.filter((id) => id !== userId)
          : [...previous, userId]
      );

      return;
    }

    setSelectedUsers((previous) =>
      previous.includes(userId)
        ? previous.filter((id) => id !== userId)
        : [...previous, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectAllGlobal) {
      setSelectAllGlobal(false);
      setSelectedUsers([]);
      setExcludedUsers([]);
      return;
    }

    setSelectAllGlobal(true);
    setSelectedUsers([]);
    setExcludedUsers([]);
  };

  const selectedCount = selectAllGlobal
    ? Math.max(0, totalRecords - excludedUsers.length)
    : selectedUsers.length;

  // -------------------------------------------------------
  // DELETE
  // -------------------------------------------------------

  const handleBulkDelete = async () => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        toast.error("Session expired");
        return;
      }

      if (selectedCount === 0) {
        setConfirmDeleteOpen(false);
        return;
      }

      setLoading(true);

      const failedIds: number[] = [];

      if (selectAllGlobal) {
        /*
          A backend bulk-delete endpoint is required to delete users
          from every paginated page while respecting excludedUsers.
        */

        toast.info(
          "Deleting all users across pages requires a bulk-delete API."
        );

        return;
      }

      for (const userId of selectedUsers) {
        try {
          await deleteUserAdmin(userId, token);
        } catch {
          failedIds.push(userId);
        }
      }

      if (failedIds.length > 0) {
        toast.warn(
          `${failedIds.length} ${failedIds.length === 1 ? "user" : "users"
          } failed to delete.`
        );
      } else {
        toast.success(
          selectedUsers.length === 1
            ? "User deleted successfully"
            : "Users deleted successfully"
        );
      }

      const response = await GetAllUsersAsync(
        pageNumber,
        pageSize,
        searchText,
        token
      );

      /*
        When the last record on the current page is deleted,
        move to the previous page.
      */
      if (
        response.data.length === 0 &&
        pageNumber > 1 &&
        response.totalRecords > 0
      ) {
        await fetchUsers(pageNumber - 1, searchText);
      } else {
        setUsers(response.data || []);
        setTotalRecords(response.totalRecords || 0);
      }

      setSelectedUsers([]);
      setSelectAllGlobal(false);
      setExcludedUsers([]);
      setConfirmDeleteOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------
  // PAGINATION
  // -------------------------------------------------------

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || loading) return;

    if (usersView === "users") {
      if (selectedRole === "all") {
        fetchUsers(page, searchText);
      } else {
        fetchFilteredUsers(page, selectedRole);
      }
    } else {
      fetchDeletedUsers(page);
    }
  };

  const getVisiblePages = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      return Array.from(
        { length: totalPages },
        (_, i) => i + 1
      );
    }

    pages.push(1);

    if (pageNumber > 4) {
      pages.push("...");
    }

    const start = Math.max(2, pageNumber - 2);
    const end = Math.min(totalPages - 1, pageNumber + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (pageNumber < totalPages - 3) {
      pages.push("...");
    }

    pages.push(totalPages);

    return pages;
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Inner tabs and actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-8">
            <button
              type="button"
              onClick={() => handleUsersViewChange("users")}
              className={`h-[36px] flex items-center text-sm font-medium border-b-2 transition ${usersView === "users"
                ? "text-blue-600 border-blue-600"
                : "text-gray-500 border-transparent hover:text-gray-700"
                }`}
            >
              Users List
              {usersView === "users" && (
                <span className="ml-1">({totalRecords})</span>
              )}
            </button>

            <button
              type="button"
              onClick={() => handleUsersViewChange("deleted")}
              className={`h-[36px] flex items-center text-sm font-medium border-b-2 transition ${usersView === "deleted"
                ? "text-blue-600 border-blue-600"
                : "text-gray-500 border-transparent hover:text-gray-700"
                }`}
            >
              Deleted Users
              {usersView === "deleted" && (
                <span className="ml-1">({totalRecords})</span>
              )}
            </button>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">

            {usersView === "users" && (
              <>
                {/* Role Filter */}
                {/* Role Filter */}
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="roleFilter"
                    className="text-sm font-medium text-gray-600 whitespace-nowrap"
                  >
                    Role
                  </label>

                  <div className="relative">
                    <select
                      id="roleFilter"
                      value={selectedRole}
                      onChange={(e) => handleRoleChange(e.target.value)}
                      className="
        h-10 w-[150px]
        appearance-none
        rounded-lg
        border border-gray-200
        bg-white
        pl-3 pr-9
        text-sm font-medium text-gray-700
        shadow-sm
        cursor-pointer
        transition-all duration-200
        hover:border-blue-300
        hover:shadow
        focus:border-blue-500
        focus:outline-none
        focus:ring-2 focus:ring-blue-100
      "
                    >
                      <option value="all">All Roles</option>
                      <option value="admin">Admin</option>
                      <option value="user">User</option>
                      <option value="apiuser">API User</option>
                      <option value="guest">Guest</option>
                    </select>

                    {/* Dropdown arrow */}
                    <svg
                      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>

                {/* Delete Selected */}
                <button
                  type="button"
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={selectedCount === 0 || loading}
                  className={`h-10 px-4 rounded-lg text-sm font-medium transition ${selectedCount === 0 || loading
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "text-red-600 border border-red-600 hover:bg-red-50"
                    }`}
                >
                  Delete Selected ({selectedCount})
                </button>
              </>
            )}

            {/* Search */}
            <div className="relative w-full sm:w-[300px]">
              <Search
                size={17}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500"
              />

              <input
                type="text"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search by name, email or phone"
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>
        </div>


        {/* Table area */}
        <div className="px-5 py-4">
          {loading ? (
            <div className="rounded-xl p-8 text-center text-gray-500">
              {usersView === "users"
                ? "Loading users..."
                : "Loading deleted users..."}
            </div>
          ) : usersView === "users" ? (
            users.length === 0 ? (
              <div className="rounded-xl p-8 text-center text-gray-500">
                No users found.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-blue-500 border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={toggleSelectAll}
                          aria-label="Select all users"
                        />
                      </th>

                      {[
                        "User Name",
                        "Email",
                        "Phone Number",
                        "Created Date",
                        "Role",
                        "Action",
                      ].map((header) => (
                        <th
                          key={header}
                          className={`px-4 py-3 text-xs font-semibold text-white uppercase tracking-wide ${header === "Action"
                            ? "text-center"
                            : "text-left"
                            }`}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr
                        key={user.id}
                        className={`transition-colors duration-150 ${isChecked(user.id)
                          ? "bg-blue-50"
                          : "hover:bg-blue-50/50"
                          }`}
                      >
                        <td className="px-4 py-4 text-left">
                          <input
                            type="checkbox"
                            checked={isChecked(user.id)}
                            onChange={() =>
                              toggleSelectUser(user.id)
                            }
                            aria-label={`Select ${user.userName || "user"
                              }`}
                          />
                        </td>

                        <td className="px-4 py-4 text-left text-[14px]">
                          <span className={user.userName ? "text-[#202124]" : "text-gray-400"}>
                            {user.userName || "Not Provided"}
                          </span>
                        </td>

                        <td className="px-4 py-4 text-left text-[14px] break-all">
                          <span className={user.email ? "text-[#202124]" : "text-gray-400"}>
                            {user.email || "Not Provided"}
                          </span>
                        </td>

                        <td className="px-4 py-4 text-left text-[14px]">
                          <span className={user.phoneNumber ? "text-[#202124]" : "text-gray-400"}>
                            {user.phoneNumber || "Not Provided"}
                          </span>
                        </td>

                        <td className="px-4 py-4 text-left text-[14px]">
                          <span className={user.createdDate ? "text-[#202124]" : "text-gray-400"}>
                            {formatDate(user.createdDate)}
                          </span>
                        </td>

                        <td className="px-4 py-4 text-left text-[14px]">
                          {user.roleName ? (
                            <span className="px-2 py-1 text-xs rounded-md bg-blue-100 text-blue-700">
                              {user.roleName}
                            </span>
                          ) : (
                            <span className="text-gray-400">
                              Not Provided
                            </span>
                          )}
                        </td>

                        <td className="px-4 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleEditClick(user)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-blue-100 transition"
                            title="Edit user"
                            aria-label={`Edit ${user.userName || "user"
                              }`}
                          >
                            <img
                              src={EditIcon}
                              alt=""
                              className="w-4 h-4"
                            />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : filteredDeletedUsers.length === 0 ? (
            <div className="rounded-xl p-8 text-center text-gray-500">
              {searchText.trim()
                ? "No matching deleted users found."
                : "No deleted users found."}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-blue-500 border-b border-gray-200">
                    {[
                      "User Name",
                      "Email",
                      "Phone Number",
                      "Created Date"
                    ].map((header) => (
                      <th
                        key={header}
                        className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {filteredDeletedUsers.map((user) => (
                    <tr
                      key={user.id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td
                        className={`px-4 py-4 text-left text-[14px] ${user.userName
                          ? "text-[#202124]"
                          : "text-gray-400"
                          }`}
                      >
                        {user.userName || "Not Provided"}
                      </td>

                      <td
                        className={`px-4 py-4 text-left text-[14px] break-all ${user.email
                          ? "text-[#202124]"
                          : "text-gray-400"
                          }`}
                      >
                        {user.email || "Not Provided"}
                      </td>

                      <td
                        className={`px-4 py-4 text-left text-[14px] ${user.phoneNumber
                          ? "text-[#202124]"
                          : "text-gray-400"
                          }`}
                      >
                        {user.phoneNumber || "Not Provided"}
                      </td>

                      <td className="px-4 py-4 text-left text-[14px] text-[#202124]">
                        {formatDate(user.createdDate)}
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalRecords > 0 && totalPages > 1 && (
          <div className="flex justify-center py-5 border-t border-gray-200">
            <div className="flex items-center gap-1 bg-white border rounded-lg shadow-sm px-2 py-1">
              <button
                type="button"
                onClick={() => handlePageChange(pageNumber - 1)}
                disabled={pageNumber === 1 || loading}
                className="px-3 py-2 text-gray-500 hover:bg-gray-100 rounded-md disabled:opacity-40"
              >
                «
              </button>

              {getVisiblePages().map((item, index) =>
                item === "..." ? (
                  <span
                    key={`dots-${index}`}
                    className="px-2 text-gray-400"
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={item}
                    onClick={() => handlePageChange(item as number)}
                    className={`min-w-[38px] h-[38px] rounded-lg text-sm font-medium transition ${pageNumber === item
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-gray-700 hover:bg-gray-100"
                      }`}
                  >
                    {item}
                  </button>
                )
              )}

              {totalPages > 5 &&
                !getVisiblePages().includes(totalPages) && (
                  <>
                    <span className="px-2 text-gray-400">...</span>

                    <button
                      type="button"
                      onClick={() => handlePageChange(totalPages)}
                      className="min-w-[36px] h-[36px] rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100"
                    >
                      {totalPages}
                    </button>
                  </>
                )}

              <button
                type="button"
                onClick={() => handlePageChange(pageNumber + 1)}
                disabled={pageNumber === totalPages || loading}
                className="px-3 py-2 text-gray-500 hover:bg-gray-100 rounded-md disabled:opacity-40"
              >
                »
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Edit User Modal */}
      {editModalOpen && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-gray-800">
                Edit User
              </h2>

              <button
                type="button"
                onClick={closeEditModal}
                disabled={updating}
                className="text-2xl leading-none text-gray-400 hover:text-gray-700 disabled:cursor-not-allowed"
                aria-label="Close edit modal"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUpdateUser}>
              <div className="space-y-4 px-5 py-5">
                <div>
                  <label
                    htmlFor="editUserName"
                    className="mb-1.5 block text-sm font-medium text-gray-700"
                  >
                    User Name
                  </label>

                  <input
                    id="editUserName"
                    type="text"
                    name="userName"
                    maxLength={30}
                    value={editForm.userName}
                    onChange={handleEditInputChange}
                    placeholder="Enter user name"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="editEmail"
                    className="mb-1.5 block text-sm font-medium text-gray-700"
                  >
                    Email
                  </label>

                  <input
                    id="editEmail"
                    type="email"
                    name="email"
                    maxLength={50}
                    value={editForm.email}
                    onChange={handleEditInputChange}
                    placeholder="Enter email address"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="editPhoneNumber"
                    className="mb-1.5 block text-sm font-medium text-gray-700"
                  >
                    Phone Number
                  </label>

                  <input
                    id="editPhoneNumber"
                    type="tel"
                    name="phoneNumber"
                    value={editForm.phoneNumber}
                    onChange={handleEditInputChange}
                    placeholder="Enter phone number"
                    maxLength={16} // + plus 15 digits
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
                <button
                  type="button"
                  onClick={closeEditModal}
                  disabled={updating}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={updating}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {updating ? "Updating..." : "Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteOpen && usersView === "users" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-6">
            <h2 className="text-lg font-semibold text-gray-800">
              Confirm Delete
            </h2>

            <p className="mt-2 text-sm text-gray-600">
              Delete {selectedCount} selected{" "}
              {selectedCount !== 1 ? "users" : "user"}?
            </p>

            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmDeleteOpen(false)}
                disabled={loading}
                className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleBulkDelete}
                disabled={loading}
                className="rounded-md bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-60"
              >
                {loading ? "Deleting..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AllUsers;